import { useEffect } from "react";
import type { AssetResponse } from "../generated/api/types.gen";
import { ExpandImagesModal } from "./ExpandImagesModal";

/** Full-screen preview of one asset, layered over an open asset modal.
 *
 *  The Escape handling is the subtle part: ModalShell and the lightbox both
 *  close on Escape from a window listener, so this claims the key in the
 *  capture phase and one press closes only the preview. */
export function AssetPreviewLightbox({
  asset,
  onClose,
  onUse,
}: {
  asset: AssetResponse | null;
  onClose: () => void;
  onUse: (asset: AssetResponse) => void;
}) {
  useEffect(() => {
    if (!asset) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [asset, onClose]);

  if (!asset) return null;

  return (
    <ExpandImagesModal
      isOpen
      onClose={onClose}
      images={[{ url: asset.value, alt: asset.name }]}
      title={asset.name}
      zClassName="z-60"
      action={{ label: "Use this asset", onClick: () => onUse(asset) }}
    />
  );
}
